import DatePicker from 'react-datepicker';
import { Pencil, WarningCircle } from 'assets/icons';
import AddressGeoApi from 'components/AddressGeoApi/AddressGeoApi';
import Button from 'components/button';
import Divider from 'components/divider/divider';
import Salary from 'components/salary/Salary';
import Schedule from 'components/schedule/schedule';
import StyledInput from 'components/styledInput/StyledInput';
import { dateWordShort } from 'helper/helper';
import MissionDetailRow from './MissionDetailRow';
import { missionDetailText } from './missionDetailTexts';

const proofPersonPrefix = (reason) =>
  (reason.proof_firstname || reason.proof_lastname) &&
  reason.proof_firstname + ' ' + reason.proof_lastname + ' - ';

const contactLine = (contact) =>
  [contact?.name, contact?.poste].filter(Boolean).join(' — ') ||
  missionDetailText('info.contactEmpty');

const PeriodDatePicker = ({ label, selected, onChange, hasError }) => (
  <div className={'w-full'}>
    <div>
      <label className="text-gray-500 flex flex-col gap-1">
        <div>{label}</div>
        <DatePicker
          dateFormat="dd/MM/yyyy"
          selected={selected}
          onChange={onChange}
          locale="fr-FR"
          peekNextMonth
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          popperPlacement="bottom"
          className={`text-sm w-full h-10 pl-3 pr-8 ft-b border rounded-lg text-center ${
            hasError ? 'border-red-500' : ''
          }`}
        />
      </label>
    </div>
  </div>
);

/**
 * « Mission » sub-tab of the mission detail. The form state lives in
 * adminMissionDetails, which compares it with the mission to build the changes.
 */
const MissionInfoTab = ({
  infoMission,
  isEditing,
  formData,
  setFormData,
  qualifications,
  onQualificationChange,
  periodDatesError,
  onSalaryValidityChange,
  address,
  onAddressChange,
  onEditReason,
  contactError,
}) => {
  const checkedQualifications = qualifications.filter(
    (qualification) => qualification.checked === true,
  );
  const setField = (field, value) =>
    setFormData((prevData) => ({ ...prevData, [field]: value }));
  const setPeriodBound = (bound, date) =>
    setFormData((prevData) => ({
      ...prevData,
      period: { ...prevData.period, [bound]: date },
    }));

  return (
    <>
      <MissionDetailRow label={'Entreprise'}>
        {infoMission.company?.name_commercial}
      </MissionDetailRow>
      <Divider />
      <MissionDetailRow label={'Qualification'}>
        {isEditing ? (
          <div className={''}>
            <StyledInput
              type={'selectMultipleRadio'}
              value={
                checkedQualifications.length > 0
                  ? checkedQualifications
                      .map((qualification) => qualification.name)
                      .join(', ')
                  : 'Qualification'
              }
              list={qualifications.map((qualification) => ({
                value: qualification._id,
                label: qualification.name,
                checked: qualification.checked,
              }))}
              onChange={onQualificationChange}
              isSearchable
            />
          </div>
        ) : (
          infoMission.title
        )}
      </MissionDetailRow>
      <Divider />
      <MissionDetailRow label={'Période'}>
        {isEditing ? (
          <div className={'flex flex-col gap-1'}>
            <div className={'flex gap-2 text-sm'}>
              <PeriodDatePicker
                label={'Date de début'}
                selected={formData.period.start}
                onChange={(date) => setPeriodBound('start', date)}
                hasError={Boolean(periodDatesError)}
              />
              <PeriodDatePicker
                label={'Date de fin'}
                selected={formData.period.end}
                onChange={(date) => setPeriodBound('end', date)}
                hasError={Boolean(periodDatesError)}
              />
            </div>
            {periodDatesError && (
              <div className={'text-red-500 flex items-center gap-1 text-xs'}>
                <WarningCircle wh={16} color={'#EF4444'} />
                {periodDatesError}
              </div>
            )}
          </div>
        ) : (
          <div className={'flex gap-1'}>
            <div className={''}>
              {dateWordShort(infoMission.duration?.datestart)}
            </div>
            <div className={''}>{'>'}</div>
            <div className={''}>
              {dateWordShort(infoMission.duration?.dateend)}
            </div>
          </div>
        )}
      </MissionDetailRow>
      {isEditing ? (
        <Schedule
          mission={infoMission}
          dateStart={formData.period.start}
          dateEnd={formData.period.end}
          schedule={formData.schedule}
          isEditable
        />
      ) : (
        <Schedule
          mission={infoMission}
          dateStart={infoMission.duration.datestart}
          dateEnd={infoMission.duration.dateend}
          schedule={infoMission.schedules}
        />
      )}
      <MissionDetailRow label={'Rémunération'}>
        {isEditing ? (
          <Salary
            salary={formData.salary}
            previousSalary={infoMission.salary?.wage_per_hour}
            hasContract={
              infoMission.contract_tempworker?.some((c) => c.id_bestt) ?? false
            }
            onValidityChange={onSalaryValidityChange}
            onChange={(value) => setField('salary', value)}
          />
        ) : (
          infoMission.salary.wage_per_hour + '€/h'
        )}
      </MissionDetailRow>
      <Divider />
      <MissionDetailRow label={'Adresse'}>
        {isEditing ? (
          <AddressGeoApi
            label={'Adresse'}
            addressGeoValue={onAddressChange}
            addressValue={address}
            isAddressCompany={infoMission.company.location}
            page={'adminMissionDetail'}
          />
        ) : (
          infoMission.address.street +
          ' ' +
          infoMission.address.postcode +
          ' ' +
          infoMission.address.city +
          ', ' +
          infoMission.address.department
        )}
      </MissionDetailRow>
      <Divider />
      <MissionDetailRow label={'Motif'}>
        {isEditing ? (
          <div
            className={
              'border rounded-lg px-2 py-3 flex justify-between text-sm text-gray-700'
            }
          >
            <div className={'flex flex-col gap-1'}>
              <div className={'font-bold'}>{formData.reason.reason}</div>
              <div className={''}>
                {proofPersonPrefix(formData.reason)} {formData.reason.proof}
              </div>
            </div>
            <div className={''}>
              <Button size={'SMI'} color={'SECONDARY'} onClick={onEditReason}>
                <Pencil wh={20} color={'#374151'} />
              </Button>
            </div>
          </div>
        ) : (
          <>
            {infoMission.reason_detail.reason} :{' '}
            {proofPersonPrefix(infoMission.reason_detail)}{' '}
            {infoMission.reason_detail.proof}
          </>
        )}
      </MissionDetailRow>
      <Divider />
      <MissionDetailRow label={'Référence'}>
        {isEditing ? (
          <StyledInput
            type={'text'}
            value={formData.reference}
            onChange={(e) => setField('reference', e.target.value)}
          />
        ) : (
          infoMission.reference || 'Aucune'
        )}
      </MissionDetailRow>
      <Divider />
      <MissionDetailRow label={'Informations pour le candidat'}>
        {isEditing ? (
          <StyledInput
            type={'textarea'}
            value={formData.other}
            onChange={(e) => setField('other', e.target.value)}
          />
        ) : (
          infoMission.other || 'Aucune'
        )}
      </MissionDetailRow>
      <Divider />
      <MissionDetailRow label={"Informations pour l'agence"}>
        {isEditing ? (
          <StyledInput
            type={'textarea'}
            value={formData.other_interim}
            onChange={(e) => setField('other_interim', e.target.value)}
          />
        ) : (
          infoMission.other_interim || 'Aucune'
        )}
      </MissionDetailRow>
      <Divider />
      <MissionDetailRow label={missionDetailText('info.contact')}>
        {isEditing ? (
          <div className={'flex flex-col gap-2'}>
            <StyledInput
              type={'text'}
              label={missionDetailText('info.contactName')}
              value={formData.contact?.name ?? ''}
              error={contactError}
              onChange={(e) =>
                setField('contact', {
                  ...formData.contact,
                  name: e.target.value,
                })
              }
            />
            <StyledInput
              type={'text'}
              label={missionDetailText('info.contactPoste')}
              value={formData.contact?.poste ?? ''}
              onChange={(e) =>
                setField('contact', {
                  ...formData.contact,
                  poste: e.target.value,
                })
              }
            />
          </div>
        ) : (
          contactLine(infoMission.contact)
        )}
      </MissionDetailRow>
      <Divider />
    </>
  );
};

export default MissionInfoTab;
